import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, X, PhoneCall, DollarSign, Mountain, Truck, Zap, MapPin, ArrowRight, ArrowLeft, Phone, ChevronDown } from 'lucide-react';
import { useT } from '../utils/i18n';
// FAQ Component
function FaqItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-stone-200 dark:border-white/10 last:border-0" data-id="element-2728" data-mp="mctjjia4q0o9">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full items-center justify-between py-5 text-left font-bold text-brand-charcoal dark:text-white hover:text-brand-orange transition-colors" data-id="element-2729" data-mp="hdz2olvq2m6s">
        <span className="text-lg" data-id="element-2730" data-mp="7k8i3s2puvwd">{question}</span>
        <ChevronDown className={`w-5 h-5 transition-transform duration-300 shrink-0 ml-4 ${isOpen ? 'rotate-180' : ''}`} data-id="element-2731" data-mp="152iaz1eruyyv" />
      </button>
      <motion.div initial={false} animate={{
      height: isOpen ? 'auto' : 0,
      opacity: isOpen ? 1 : 0
    }} className="overflow-hidden" data-id="element-2732" data-mp="108nip9l3pbo6">
        <p className="pb-5 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2733" data-mp="2k2y3dunbbg5z">
          {answer}
        </p>
      </motion.div>
    </div>;
}
export function LocalVsNational() {
  const t = useT();
  // Carousel state for the "Why Local Matters" split panel (replaces the
  // old 6-card grid). Steps through the same benefits one at a time.
  const [whyIdx, setWhyIdx] = useState(0);
  const [whyDir, setWhyDir] = useState(1);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const comparisonData = [{
    attribute: t('lvn.adv.1.attr'),
    local: t('lvn.adv.1.local'),
    national: t('lvn.adv.1.nat')
  }, {
    attribute: t('lvn.adv.2.attr'),
    local: t('lvn.adv.2.local'),
    national: t('lvn.adv.2.nat')
  }, {
    attribute: t('lvn.adv.3.attr'),
    local: t('lvn.adv.3.local'),
    national: t('lvn.adv.3.nat')
  }, {
    attribute: t('lvn.adv.4.attr'),
    local: t('lvn.adv.4.local'),
    national: t('lvn.adv.4.nat')
  }, {
    attribute: t('lvn.adv.5.attr'),
    local: t('lvn.adv.5.local'),
    national: t('lvn.adv.5.nat')
  }, {
    attribute: t('lvn.adv.6.attr'),
    local: t('lvn.adv.6.local'),
    national: t('lvn.adv.6.nat')
  }, {
    attribute: t('lvn.adv.7.attr'),
    local: t('lvn.adv.7.local'),
    national: t('lvn.adv.7.nat')
  }];
  const benefits = [{
    title: t('lvn.why.1.title'),
    desc: t('lvn.why.1.desc'),
    icon: PhoneCall
  }, {
    title: t('lvn.why.2.title'),
    desc: t('lvn.why.2.desc'),
    icon: DollarSign
  }, {
    title: t('lvn.why.3.title'),
    desc: t('lvn.why.3.desc'),
    icon: Mountain
  }, {
    title: t('lvn.why.4.title'),
    desc: t('lvn.why.4.desc'),
    icon: Truck
  }, {
    title: t('lvn.why.5.title'),
    desc: t('lvn.why.5.desc'),
    icon: Zap
  }, {
    title: t('lvn.why.6.title'),
    desc: t('lvn.why.6.desc'),
    icon: MapPin
  }];
  return <div className="bg-[#f8f8e3] dark:bg-[#222222] min-h-screen transition-colors duration-300" data-id="element-2734" data-mp="kty4qnhbpoum">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-id="element-2735" data-mp="t683a7xhlpgw">
        <div className="absolute inset-0 z-0" data-id="element-2736" data-mp="x60jn6t4gzwt">
          <img src="https://cdn.magicpatterns.com/uploads/eghevqpM2SdfKP6Caac4jm/file_000000008aac723095504e05a889ad89.png" alt="Dump truck delivery" className="w-full h-full object-cover" data-id="element-2737" data-mp="124af7iire2iy" />
          <div className="absolute inset-0 bg-black/70 dark:bg-black/80" data-id="element-2738" data-mp="1c39bs2q927pl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2739" data-mp="1g4l4wi8c1b2j">
          <div className="max-w-3xl" data-id="element-2740" data-mp="1g2i4d49jzzjk">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-block py-1.5 px-4 rounded-xl bg-brand-orange/20 text-brand-orange text-sm font-bold tracking-wider uppercase mb-6" data-id="element-2741" data-mp="1kut7sh3i63gv">
              {t('lvn.hero.eyebrow')}
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold text-white mb-6 leading-tight" data-id="element-2742" data-mp="1zqhi8nzezmt7">
              {t('lvn.hero.title1')} <br data-id="element-2743" data-mp="31axm8cwh4oxq" />
              <span className="text-[#03943D]" data-id="element-2744" data-mp="31afi6u3kl6f9">{t('lvn.hero.title2')}</span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-lg md:text-xl text-stone-300 leading-relaxed mb-8 font-medium font-sub" data-id="element-2745" data-mp="2dkbeynuan8os">
              {t('lvn.hero.body')}
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4" data-id="element-2746" data-mp="2imaka39lgfod">
              <Link to="/#materials" className="inline-flex items-center justify-center px-6 py-4 hover:bg-brand-orangeHover text-white rounded-xl font-bold text-base sm:text-lg transition-colors shadow-lg shadow-brand-orange/20 bg-[#03943D]" data-id="element-2747" data-mp="5d84xd71a0zc">
                {t('lvn.hero.btnQuote')}
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-2748" data-mp="dvb28hqvtn9" />
              </Link>
              <a href="tel:206-619-3742" className="inline-flex items-center justify-center px-6 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white rounded-xl font-bold text-base sm:text-lg transition-colors" data-id="element-2749" data-mp="o3zcqabdhjbm">
                <Phone className="w-5 h-5 mr-2" data-id="element-2750" data-mp="ed4sfbhostst" />
                {t('lvn.hero.btnCall')}
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-20 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2751" data-mp="171abzux08cr2">
        <div className="text-center mb-16" data-id="element-2752" data-mp="1bulitw1tgtiz">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-4" data-id="element-2753" data-mp="1bvek4hi0aqtq">
            {t('lvn.advantage.title')}
          </h2>
          <div className="w-16 h-1.5 bg-brand-orange rounded-full mx-auto mb-6" data-id="element-2754" data-mp="1gqz0r8jgoz00"></div>
          <p className="text-lg text-stone-600 dark:text-stone-300 max-w-2xl mx-auto font-sub" data-id="element-2755" data-mp="1cnj80naijisl">
            {t('lvn.advantage.subtitle')}
          </p>
        </div>

        {/* Unified comparison list — no table chrome, no pill-styled
            sub-labels. One row per attribute: small attribute label up top,
            then a two-column side-by-side answer (local | national) below.
            Works the same way on mobile and desktop. */}
        <div className="max-w-5xl mx-auto" data-id="element-2756" data-mp="syindbao5v71">
          {/* Compact column header — just two readable labels, no pills,
           no table TH. The "vs." mid-divider clarifies the relationship
           without dressing it up as table chrome. */}
          <div className="grid grid-cols-[1fr_auto_1fr] gap-3 sm:gap-6 items-baseline pb-4 mb-6 border-b border-stone-200 dark:border-white/10" data-id="element-2757" data-mp="f2535uer3fov">
            <div data-id="element-2758" data-mp="2qqte0p621hbd">
              <p className="text-[10px] sm:text-xs font-bold text-brand-orange uppercase tracking-[0.15em]" data-id="element-2759" data-mp="2vna4hq0q87rm">
                206Gravel
              </p>
              <p className="text-sm sm:text-base font-extrabold text-brand-charcoal dark:text-white leading-tight mt-0.5" data-id="element-2760" data-mp="30n4wxy05h5es">
                {t('lvn.advantage.th2')}
              </p>
            </div>
            <span className="text-xs sm:text-sm font-bold text-stone-400 dark:text-stone-500 italic px-1 sm:px-3" data-id="element-2761" data-mp="27zioabc2fal2">
              vs.
            </span>
            <div data-id="element-2762" data-mp="2gy46g5dmknlb">
              <p className="text-[10px] sm:text-xs font-bold text-stone-500 dark:text-stone-400 uppercase tracking-[0.15em]" data-id="element-2763" data-mp="2gxkztktfdlml">
                The Alternative
              </p>
              <p className="text-sm sm:text-base font-extrabold text-stone-500 dark:text-stone-400 leading-tight mt-0.5" data-id="element-2764" data-mp="1p7q14xjogjl9">
                {t('lvn.advantage.th3')}
              </p>
            </div>
          </div>

          {/* Comparison rows. Each row: attribute label, then local vs.
           national answer side-by-side. Subtle row dividers, no nested
           card chrome. */}
          <div className="divide-y divide-stone-200 dark:divide-white/10" data-id="element-2765" data-mp="wjx1dc12681j">
            {comparisonData.map((row, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.4,
            delay: idx * 0.04,
            ease: 'easeOut'
          }} className="py-5 sm:py-6" data-id="element-2766" data-mp="rl3kd9mkbywm" data-mpi={idx}>
                {/* Attribute label */}
                <p className="text-[11px] sm:text-xs font-bold text-brand-muted dark:text-stone-400 uppercase tracking-[0.12em] mb-3" data-id="element-2767" data-mp="1accwlqnpx1rw">
                  {row.attribute}
                </p>
                {/* Side-by-side answers */}
                <div className="grid grid-cols-[1fr_auto_1fr] gap-3 sm:gap-6 items-start" data-id="element-2768" data-mp="1k7ekyka65ztf">
                  <div className="flex items-start gap-2 sm:gap-3" data-id="element-2769" data-mp="22ykzk7zu6249">
                    <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-2770" data-mp="27vtmsmzucwcd">
                      <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-green-600 dark:text-green-400" data-id="element-2771" data-mp="1tz7stn33jrn8" />
                    </div>
                    <span className="text-sm sm:text-base font-semibold text-brand-charcoal dark:text-white leading-snug" data-id="element-2772" data-mp="1k7k1dm6jialr">
                      {row.local}
                    </span>
                  </div>
                  {/* Vertical divider — sits in the middle column on all sizes */}
                  <div className="w-px self-stretch bg-stone-200 dark:bg-white/10" aria-hidden="true" data-id="element-2773" data-mp="1f7xr9lm0dah6" />
                  <div className="flex items-start gap-2 sm:gap-3 opacity-70" data-id="element-2774" data-mp="118pq4n6absac">
                    <div className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-stone-100 dark:bg-white/10 flex items-center justify-center" data-id="element-2775" data-mp="11asez3vpbfdh">
                      <X className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-stone-500 dark:text-stone-400" data-id="element-2776" data-mp="1kvnu8g2dscd7" />
                    </div>
                    <span className="text-sm sm:text-base font-medium text-stone-500 dark:text-stone-400 leading-snug" data-id="element-2777" data-mp="1oz2e5htfxcvx">
                      {row.national}
                    </span>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pullquote Band */}
      <section className="py-24 bg-brand-charcoal dark:bg-black text-center px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-2778" data-mp="23orgvhh6loyb">
        <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/pFhc3AjvRgqipTxvhW5pi7/file_000000003d4c720cb3b00791865cbb87.png')] bg-cover bg-center opacity-10 mix-blend-luminosity" data-id="element-2779" data-mp="1twvfnxjstipv" />
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-2780" data-mp="2mkrbl01jn6ll">
          <Mountain className="w-12 h-12 text-brand-orange mx-auto mb-8 opacity-80" data-id="element-2781" data-mp="2mj6i7dqyttpb" />
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-white leading-relaxed mb-8" data-id="element-2782" data-mp="2rdaqq1fvq9si">
            {t('lvn.quote')}
          </h2>
          <div className="w-24 h-1.5 bg-brand-orange rounded-full mx-auto" data-id="element-2783" data-mp="ayfpb1ai6dl4"></div>
        </div>
      </section>

      {/* Benefit Deep Dive */}
      <section className="py-20 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2784" data-mp="oosyt831m0ck">
        <div className="text-center mb-16" data-id="element-2785" data-mp="kosbexxyuetd">
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-4" data-id="element-2786" data-mp="kq3cgq0118gs">
            {t('lvn.why.title')}
          </h2>
          <div className="w-16 h-1.5 bg-brand-orange rounded-full mx-auto mb-6" data-id="element-2787" data-mp="3o60nt29xcmgp"></div>
        </div>

        {(() => {
        const total = benefits.length;
        const active = benefits[whyIdx];
        const ActiveIcon = active.icon;
        const go = (dir: number) => {
          setWhyDir(dir);
          setWhyIdx(i => (i + dir + total) % total);
        };
        return <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.5,
          ease: 'easeOut'
        }} className="rounded-2xl overflow-hidden shadow-sm grid md:grid-cols-12 max-w-6xl mx-auto" data-id="element-2788" data-mp="1cfoj7k565t9s">
              {/* LEFT — headline panel (very dark green) */}
              <div className="md:col-span-5 p-8 sm:p-10 lg:p-12 flex flex-col justify-center bg-[#57534e]" data-id="element-2789" data-mp="nz5ufvi4pixg">
                <AnimatePresence mode="wait" data-id="element-2790" data-mp="3nekbm72nves0">
                  <motion.div key={`why-head-${whyIdx}`} initial={{
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -12
              }} transition={{
                duration: 0.35,
                ease: [0.16, 1, 0.3, 1]
              }} data-id="element-2791" data-mp="3rj0hi8c9ym78">
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-extrabold tracking-tight leading-[1.1] text-[#CCC0B6]" data-id="element-2792" data-mp="38qpi4ogtx6dv">
                      {active.title}
                    </h3>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* RIGHT — paragraph panel + arrows (dark green) */}
              <div className="md:col-span-7 p-8 sm:p-10 lg:p-12 flex flex-col min-h-[260px] bg-[#403C38]" data-id="element-2793" data-mp="2rbn0vp5ju2dd">
                <div className="flex-1 flex items-center" data-id="element-2794" data-mp="2i93z78di1vsp">
                  <AnimatePresence mode="wait" data-id="element-2795" data-mp="2iaotqxztdeqk">
                    <motion.p key={`why-body-${whyIdx}`} initial={{
                  opacity: 0,
                  x: whyDir * 24
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: whyDir * -24
                }} transition={{
                  duration: 0.35,
                  ease: [0.16, 1, 0.3, 1]
                }} className="text-white/90 text-lg sm:text-xl leading-relaxed" data-id="element-2796" data-mp="2dinlro9oia6c">
                      {active.desc}
                    </motion.p>
                  </AnimatePresence>
                </div>

                <div className="flex items-center justify-between mt-8 gap-[16px]" data-id="element-2797" data-mp="2i8b2d3opjq1">
                  <span className="text-white/40 text-xs font-bold tabular-nums tracking-wider" data-id="element-2798" data-mp="3kw7pkygpbh6o">
                    {String(whyIdx + 1).padStart(2, '0')} /{' '}
                    {String(total).padStart(2, '0')}
                  </span>
                  <div className="flex items-center gap-2 shrink-0" data-id="element-2799" data-mp="3ax0cmcdvpok3">
                    <button type="button" onClick={() => go(-1)} aria-label="Previous benefit" className="w-[30px] h-[30px] rounded-md text-white flex items-center justify-center transition-colors hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 bg-[#57534e]" data-id="element-2800" data-mp="3fxefzh4jcabs">
                      <ArrowLeft className="w-[15px] h-[15px]" data-id="element-2801" data-mp="105h20nmgfkzf" />
                    </button>
                    <button type="button" onClick={() => go(1)} aria-label="Next benefit" className="w-[30px] h-[30px] rounded-md text-white flex items-center justify-center transition-colors hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 bg-[#57534e]" data-id="element-2802" data-mp="155d6sp2z7gg1">
                      <ArrowRight className="w-[15px] h-[15px]" data-id="element-2803" data-mp="1o1ltzka6nqk7" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>;
      })()}
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-stone-100 dark:bg-[#1b1b1b] border-t border-stone-200 dark:border-white/5" data-id="element-2804" data-mp="1xxyim3qvfe83">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2805" data-mp="1szx29qktxt0b">
          <div className="text-center mb-12" data-id="element-2806" data-mp="2707vh09m3jon">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-4" data-id="element-2807" data-mp="26xtz8o3741fi">
              {t('lvn.faq.title')}
            </h2>
            <div className="w-16 h-1.5 bg-brand-orange rounded-full mx-auto" data-id="element-2808" data-mp="cmoqj5d94ytb"></div>
          </div>

          <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 p-6 md:p-8 shadow-sm" data-id="element-2809" data-mp="8lmxj69xhizs">
            <FaqItem question={t('lvn.faq.1.q')} answer={t('lvn.faq.1.a')} data-id="element-2810" data-mp="27rg05r08jufv" />
            <FaqItem question={t('lvn.faq.2.q')} answer={t('lvn.faq.2.a')} data-id="element-2811" data-mp="2coeti7c2rxd0" />
            <FaqItem question={t('lvn.faq.3.q')} answer={t('lvn.faq.3.a')} data-id="element-2812" data-mp="2hm8194t10zfe" />
            <FaqItem question={t('lvn.faq.4.q')} answer={t('lvn.faq.4.a')} data-id="element-2813" data-mp="2mmvpjipvf25x" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2814" data-mp="2qnny09qx5315">
        <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-brand-charcoal dark:text-white mb-6" data-id="element-2815" data-mp="2vlfxbotw7rfd">
          {t('lvn.cta.title')}
        </h2>
        <p className="text-xl text-stone-600 dark:text-stone-300 mb-10 max-w-2xl mx-auto font-sub" data-id="element-2816" data-mp="35ddgbhr57non">
          {t('lvn.cta.body')}
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2817" data-mp="3adrmsvd3ipkk">
          <Link to="/#materials" className="inline-flex items-center justify-center px-8 py-4 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-xl font-bold text-lg transition-colors shadow-lg shadow-brand-orange/20" data-id="element-2818" data-mp="3eejwfppvqwhd">
            {t('lvn.cta.btnQuote')}
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-2819" data-mp="tp8h5lywbe7q" />
          </Link>
          <a href="tel:206-619-3742" className="inline-flex items-center justify-center px-8 py-4 bg-stone-200 dark:bg-white/10 hover:bg-stone-300 dark:hover:bg-white/20 text-brand-charcoal dark:text-white rounded-xl font-bold text-lg transition-colors" data-id="element-2820" data-mp="trkypy0qrmk9">
            <Phone className="w-5 h-5 mr-2" data-id="element-2821" data-mp="1het5kkdtj3oe" />
            {t('lvn.cta.btnCall')}
          </a>
        </div>
      </section>
    </div>;
}